import { z } from 'zod';

import {
  actorSnapshotSchema,
  attachmentMetadataSchema,
  entityIdSchema,
  nonEmptyTextSchema,
  sha256Schema,
  utcDateTimeSchema,
} from './primitives';
import { resourceScopeSchema } from './tenant-rbac';

export const documentKindSchema = z.enum([
  'inspection',
  'special-inspection',
  'contractor-daily-inspection',
  'tbm',
  'risk-assessment',
  'work-permit',
  'work-plan',
  'work-procedure',
  'manual',
  'safety-education',
  'incident',
  'emergency-drill',
  'chemical-msds',
  'equipment-record',
  'sling-qualification',
  'safety-budget',
  'material-request',
  'meeting-minutes',
  'notice',
  'improvement-request',
]);

export const documentLifecycleSchema = z.enum([
  'draft',
  'submitted',
  'in-review',
  'approved',
  'rejected',
  'withdrawn',
  'superseded',
  'archived',
]);

export const templateReferenceSchema = z
  .object({
    templateId: entityIdSchema,
    templateVersionId: entityIdSchema,
    titleSnapshot: nonEmptyTextSchema.max(300),
  })
  .strict();

export const documentIntegritySchema = z
  .object({
    algorithm: z.literal('sha256'),
    contentHash: sha256Schema,
    lockedAt: utcDateTimeSchema,
    lockedBy: entityIdSchema,
  })
  .strict();

const documentEnvelopeShape = {
  schemaVersion: z.literal(1),
  documentId: entityIdSchema,
  documentVersionId: entityIdSchema,
  documentKind: documentKindSchema,
  documentNumber: z.string().trim().min(1).max(100).nullable(),
  revision: z.number().int().positive(),
  previousVersionId: entityIdSchema.nullable(),
  scope: resourceScopeSchema,
  lifecycle: documentLifecycleSchema,
  template: templateReferenceSchema.nullable(),
  author: actorSnapshotSchema,
  attachments: z.array(attachmentMetadataSchema),
  relatedDocumentIds: z.array(entityIdSchema),
  createdAt: utcDateTimeSchema,
  updatedAt: utcDateTimeSchema,
  submittedAt: utcDateTimeSchema.nullable(),
  approvedAt: utcDateTimeSchema.nullable(),
  integrity: documentIntegritySchema.nullable(),
};

type DocumentInvariantInput = {
  documentVersionId: string;
  revision: number;
  previousVersionId: string | null;
  lifecycle: z.infer<typeof documentLifecycleSchema>;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  integrity: z.infer<typeof documentIntegritySchema> | null;
};

function validateDocumentInvariants(
  document: DocumentInvariantInput,
  context: z.RefinementCtx,
): void {
  if (document.revision === 1 && document.previousVersionId !== null) {
    context.addIssue({
      code: 'custom',
      path: ['previousVersionId'],
      message: 'The first revision cannot have a previous version',
    });
  }

  if (document.revision > 1 && document.previousVersionId === null) {
    context.addIssue({
      code: 'custom',
      path: ['previousVersionId'],
      message: 'A later revision must reference its previous version',
    });
  }

  if (document.previousVersionId === document.documentVersionId) {
    context.addIssue({
      code: 'custom',
      path: ['previousVersionId'],
      message: 'A document version cannot reference itself',
    });
  }

  const submittedLifecycle = ['submitted', 'in-review', 'approved', 'rejected', 'superseded'];
  if (submittedLifecycle.includes(document.lifecycle) && document.submittedAt === null) {
    context.addIssue({
      code: 'custom',
      path: ['submittedAt'],
      message: 'Submitted document lifecycles require submittedAt',
    });
  }

  const lockedLifecycle = ['approved', 'superseded'];
  if (lockedLifecycle.includes(document.lifecycle)) {
    if (document.approvedAt === null) {
      context.addIssue({
        code: 'custom',
        path: ['approvedAt'],
        message: 'Approved document versions require approvedAt',
      });
    }
    if (document.integrity === null) {
      context.addIssue({
        code: 'custom',
        path: ['integrity'],
        message: 'Approved document versions must be integrity locked',
      });
    }
  } else if (document.approvedAt !== null || document.integrity !== null) {
    context.addIssue({
      code: 'custom',
      path: ['integrity'],
      message: 'Only approved or superseded versions may carry approval lock evidence',
    });
  }

  if (Date.parse(document.updatedAt) < Date.parse(document.createdAt)) {
    context.addIssue({
      code: 'custom',
      path: ['updatedAt'],
      message: 'updatedAt cannot precede createdAt',
    });
  }
}

export const documentEnvelopeSchema = z
  .object({ ...documentEnvelopeShape, payload: z.unknown() })
  .strict()
  .superRefine(validateDocumentInvariants);

export function createDocumentEnvelopeSchema<TPayload extends z.ZodType>(payloadSchema: TPayload) {
  return z
    .object({ ...documentEnvelopeShape, payload: payloadSchema })
    .strict()
    .superRefine(validateDocumentInvariants);
}

export type DocumentEnvelope<TPayload = unknown> = Omit<
  z.infer<typeof documentEnvelopeSchema>,
  'payload'
> & { payload: TPayload };
export type DocumentKind = z.infer<typeof documentKindSchema>;
export type DocumentLifecycle = z.infer<typeof documentLifecycleSchema>;
